/*
 * Copyright (c) 2022, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import { absoluteUrl } from 'msp-integrations'
import { resolveSiteFromUrl, getSites } from '@/utils/site-utils'
import { getConfig } from '@salesforce/pwa-kit-runtime/utils/ssr-config'

/**
 * Given your application's configuration this function returns a set of regular expressions used to match the site
 * and locale references from an url.
 * @param config
 * @return {{searchMatcherForSite: RegExp, searchMatcherForLocale: RegExp, pathMatcher: RegExp}}
 */
export const getConfigMatcher = (config) => {
  if (!config) {
    throw new Error('Config is not defined.')
  }

  const allSites = getSites()
  const siteIds = []
  const siteAliases = []
  const localesIds = []
  const localeAliases = []
  allSites.forEach((site) => {
    siteAliases.push(site.alias)
    siteIds.push(site.id)
    const { l10n } = site
    l10n.supportedLocales.forEach((locale) => {
      localesIds.push(locale.id)
      localeAliases.push(locale.alias)
    })
  })
  const sites = [...siteIds, ...siteAliases].filter(Boolean)
  const locales = [...localesIds, ...localeAliases].filter(Boolean)

  // prettier-ignore
  // eslint-disable-next-line
  const searchPatternForSite = `site=(?<site>${sites.join('|')})`
  // prettier-ignore
  // eslint-disable-next-line
  const pathPattern = `(?:\/(?<site>${sites.join('|')}))?(?:\/(?<locale>${locales.join("|")}))?(?!\\w)`
  // prettier-ignore
  // eslint-disable-next-line
  const searchPatternForLocale = `locale=(?<locale>${locales.join('|')})`
  const pathMatcher = new RegExp(pathPattern)
  const searchMatcherForSite = new RegExp(searchPatternForSite)
  const searchMatcherForLocale = new RegExp(searchPatternForLocale)
  return {
    pathMatcher,
    searchMatcherForSite,
    searchMatcherForLocale
  }
}

/**
 * This function return the identifiers (site and locale) from the given url
 * The site will always go before locale if both of them are presented in the pathname
 * @param path {string}
 * @returns {{siteRef: string, localeRef: string}} - site and locale reference (it could either be id or alias)
 */
export const getParamsFromPath = (path) => {
  const { pathname, search } = new URL(absoluteUrl(path))

  const config = getConfig()
  const { pathMatcher, searchMatcherForSite, searchMatcherForLocale } =
    getConfigMatcher(config)
  const pathMatch = pathname.match(pathMatcher)
  const searchMatchForSite = search.match(searchMatcherForSite)
  const searchMatchForLocale = search.match(searchMatcherForLocale)

  // the value can only either in the path or search query param, there will be no overridden
  const siteRef = pathMatch?.groups.site || searchMatchForSite?.groups.site

  const localeRef =
    pathMatch?.groups.locale || searchMatchForLocale?.groups.locale
  return { siteRef, localeRef }
}

/**
 * This function returns the url config from the current configuration
 * @return {object} - url config
 */
export const getUrlConfig = () => {
  const { app } = getConfig()
  if (!app.url) {
    throw new Error(
      'Cannot find `url` key. Please check your configuration file.'
    )
  }
  return app.url
}

/**
 * Determine the locale object from an url
 * If the localeRef is not found from the url, set it to default locale of the current site
 * and use it to find the locale object
 *
 * @param url
 * @return {Object} locale object
 */
export const resolveLocaleFromUrl = (url) => {
  if (!url) {
    throw new Error('URL is required to look for the locale object')
  }
  let { localeRef } = getParamsFromPath(url)
  const site = resolveSiteFromUrl(url)
  const { supportedLocales } = site.l10n
  // if no localeRef is found, use the default value of the current site
  if (!localeRef) {
    localeRef = site.l10n.defaultLocale
  }
  const locale = supportedLocales.find(
    (locale) => locale.alias === localeRef || locale.id === localeRef
  )
  if (locale) {
    return locale
  }
  // if locale is not defined, use default locale as fallback value
  const defaultLocale = site.l10n.defaultLocale
  return supportedLocales.find(
    (locale) => locale.alias === defaultLocale || locale.id === defaultLocale
  )
}
